import React, { useCallback, useEffect, useRef, useState } from 'react';
import DashboardReturnLink from './DashboardReturnLink';
import { DateTime } from 'luxon';
import { FiArrowLeft, FiChevronDown, FiExternalLink, FiEyeOff, FiMessageCircle, FiSmartphone, FiSmile, FiX } from 'react-icons/fi';
import StudentAskBar from './StudentAskBar';
import { hasInstructorReply, isInstructorReply } from './qnaHelpers';
import styles from './StudentQnaPanel.module.css';

const NAME_COLORS = ['#2F6470', '#8C5638', '#6B4E9B', '#A04668', '#3E7A4F', '#9A6B1F'];

function nameColor(name) {
  let h = 0;
  String(name || '').split('').forEach((c) => { h = (h * 31 + c.charCodeAt(0)) % 997; });
  return NAME_COLORS[h % NAME_COLORS.length];
}

function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
}

// Round initials avatar for other people's messages; anonymous gets a crossed-out eye.
function Avatar({ item, instructor }) {
  if (item.isAnonymous) return <span className={`${styles.avatarSm} ${styles.avatarAnon}`} aria-hidden="true"><FiEyeOff size={15} /></span>;
  const color = instructor ? '#17382F' : nameColor(item.authorName);
  return (
    <span className={styles.avatarSm} style={{ color, background: instructor ? '#E9F0EC' : `${color}33` }} aria-hidden="true">
      {initials(item.authorName)}
    </span>
  );
}

function formatTime(value) {
  const dt = DateTime.fromJSDate(new Date(value)).setZone('America/Chicago');
  return dt.isValid ? dt.toFormat('h:mm a') : '';
}

const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;

function Linkified({ text }) {
  return String(text).split(URL_PATTERN).map((part, i) => {
    if (i % 2 === 0) return part;
    const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer nofollow" className={styles.link}>{part}</a>;
  });
}

const EMOJI = { like: '👍', heart: '❤️' };
const LABEL = { like: 'Like', heart: 'Heart' };

// Existing reactions: only the ones that have been used, hanging off the bubble.
function ReactionPill({ item, disabled, onReact }) {
  const used = ['like', 'heart'].filter((t) => (item.reactions?.[t] || 0) > 0);
  if (used.length === 0) return null;
  return (
    <div className={styles.reactRow}>
      {used.map((type) => (
        <button
          key={type}
          type="button"
          className={`${styles.react} ${item.myReaction === type ? styles.reactOn : ''}`}
          disabled={disabled}
          aria-pressed={item.myReaction === type}
          aria-label={LABEL[type]}
          onClick={() => onReact(type)}
        >
          <span aria-hidden="true">{EMOJI[type]}</span>{item.reactions[type]}
        </button>
      ))}
    </div>
  );
}

// Smiley that appears on hover (or tap) and opens the two-emoji picker.
function ReactPicker({ open, item, onToggle, onReact }) {
  return (
    <span className={styles.pickerWrap}>
      <button type="button" className={styles.addReact} aria-label="Add reaction" aria-expanded={open} onClick={onToggle}>
        <FiSmile size={14} />
      </button>
      {open && (
        <span className={styles.picker} role="group" aria-label="Pick a reaction">
          {['like', 'heart'].map((type) => (
            <button
              key={type}
              type="button"
              className={`${styles.pick} ${item.myReaction === type ? styles.pickOn : ''}`}
              aria-label={LABEL[type]}
              aria-pressed={item.myReaction === type}
              onClick={() => onReact(type)}
            >
              {EMOJI[type]}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

function Bubble({ item, kind, quote, isOpen, onReact, onStartEdit, onDelete, onReply, beingEdited }) {
  const [picking, setPicking] = useState(false);
  const [menu, setMenu] = useState(null); // null | 'menu' | 'confirm'
  const [menuPos, setMenuPos] = useState({});
  // The options menu stays open until you pick something, click elsewhere or press Escape.
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (e.type === 'keydown' ? e.key === 'Escape' : !e.target.closest?.('[data-qna-menu]')) setMenu(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [menu]);
  const react = (type) => { onReact(type); setPicking(false); };
  const out = Boolean(item.isMine);
  const isReply = kind === 'reply';
  const byInstructor = isReply && isInstructorReply(item);
  const canEdit = out && isOpen;
  const targetType = isReply ? 'replies' : 'questions';

  return (
    <div className={`${styles.row} ${out ? styles.rowOut : styles.rowIn}`} onMouseLeave={() => setPicking(false)}>
      <div className={styles.line}>
      {!out && <Avatar item={item} instructor={byInstructor} />}
      <div
        className={`${styles.bubble} ${out ? styles.bubbleOut : ''} ${beingEdited ? styles.bubbleEditing : ''}`}
        onClick={(e) => { if (isOpen && window.matchMedia?.('(hover: none)').matches && !e.target.closest('a, button')) setPicking((v) => !v); }}
      >
        {!out && (
          <div className={styles.sender} style={{ color: byInstructor ? undefined : nameColor(item.authorName) }}>
            {item.authorName}
            {byInstructor && <span className={styles.role}>Instructor</span>}
          </div>
        )}
        {quote && (
          <div className={styles.quote}>
            <b>{quote.authorName}</b>
            <span>{quote.body}</span>
          </div>
        )}
        <div className={styles.bodyLine}>
          <span className={styles.text} dir="auto"><Linkified text={item.body} />{item.editedAt && <span className={styles.edited}> (edited)</span>}</span>
          <span className={styles.meta}>
            {formatTime(item.createdAt)}
          </span>
        </div>
      </div>
      {isOpen && (
        <div className={styles.menuWrap} data-qna-menu>
          <button
            type="button"
            className={styles.chevron}
            aria-label="Message options"
            aria-expanded={Boolean(menu)}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
              setMenu((m) => (m ? null : 'menu'));
            }}
          >
            <FiChevronDown size={16} />
          </button>
          {menu === 'menu' && (
            <div className={styles.menu} data-qna-menu style={menuPos} role="menu">
              <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { setMenu(null); onReply(); }}>Reply</button>
              {canEdit && <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { setMenu(null); onStartEdit(item); }}>Edit</button>}
              {canEdit && <button type="button" role="menuitem" className={styles.menuItem} onClick={() => setMenu('confirm')}>Delete</button>}
            </div>
          )}
          {menu === 'confirm' && (
            <div className={styles.menu} data-qna-menu style={menuPos} role="alertdialog" aria-label="Delete this message?">
              <span className={styles.confirmText}>{isReply ? 'Delete this reply?' : 'Delete this question and its replies?'}</span>
              <div className={styles.confirmRow}>
                <button type="button" className={styles.menuItem} onClick={() => setMenu(null)}>Cancel</button>
                <button type="button" className={`${styles.menuItem} ${styles.menuDanger}`} onClick={() => { setMenu(null); onDelete(targetType, item.id); }}>Delete</button>
              </div>
            </div>
          )}
        </div>
      )}
      {isOpen && <ReactPicker open={picking} item={item} onToggle={() => setPicking((v) => !v)} onReact={react} />}
      </div>
      <ReactionPill item={item} disabled={!isOpen} onReact={onReact} />
    </div>
  );
}

// Student view of the Session Q&A: a chat thread with a chat-style ask bar.
export default function StudentQnaPanel({
  title, fullPage, onClose, onPopOut, qrNode, showQr, onToggleQr, toast, onDismissToast,
  questions, isOpen, loading, error, posting, postQuestion, postReply, toggleReaction, editMessage, deleteMessage, courseCode, weekLabel,
}) {
  const label = [courseCode, weekLabel].filter(Boolean).join(' · ');
  const heading = label ? `${label} Q&A` : (title || 'Session Q&A');
  const count = questions.length;
  const threadRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const startEdit = (item, targetType) => { setReplyingTo(null); setEditing({ id: item.id, body: item.body, targetType }); };
  const startReply = (questionId, target, replyToId) => {
    setEditing(null);
    setReplyingTo({ questionId, replyToId, authorName: target.authorName, body: target.body });
  };
  const saveEdit = useCallback(async (body) => {
    const ok = await editMessage(editing.targetType, editing.id, body);
    if (ok) setEditing(null);
  }, [editMessage, editing]);
  const sendReply = useCallback(async (body, isAnonymous) => {
    const { questionId, replyToId } = replyingTo;
    setReplyingTo(null);
    await postReply(questionId, body, { replyToId, isAnonymous });
  }, [postReply, replyingTo]);
  const replyTotal = questions.reduce((n, q) => n + q.replies.length, 0);
  // Like a chat, keep the newest message in view.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, replyTotal, loading]);

  return (
    <div className={`${styles.card} ${fullPage ? styles.cardFull : ''}`}>
      <div className={styles.header}>
        {fullPage && <DashboardReturnLink className={styles.iconBtn} aria-label="Back to dashboard"><FiArrowLeft size={20} /></DashboardReturnLink>}
        <span className={styles.avatar}><FiMessageCircle size={20} /></span>
        <div className={styles.headText}>
          <b>{heading}</b>
          <span>{loading ? ' ' : `${count} question${count === 1 ? '' : 's'} · ${isOpen ? 'live' : 'closed'}`}</span>
        </div>
        <div className={styles.headActions}>
          <button type="button" className={styles.iconBtn} onClick={onToggleQr} aria-label="Show QR code for mobile" aria-expanded={showQr} title="Open on phone (QR code)"><FiSmartphone size={18} /></button>
          {!fullPage && <button type="button" className={styles.iconBtn} onClick={onPopOut} aria-label="Open in separate window" title="Open in separate window"><FiExternalLink size={18} /></button>}
          {onClose && <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close"><FiX size={18} /></button>}
        </div>
      </div>
      {showQr && qrNode}

      <div className={styles.thread} ref={threadRef}>
        {toast && <button type="button" className={styles.sysChip} role="status" aria-live="polite" onClick={onDismissToast}>{toast}</button>}
        {!loading && !isOpen && <div className={styles.sysChip}>Q&A is closed for this session. You can still read the thread.</div>}
        {error && <div className={`${styles.sysChip} ${styles.sysError}`}>{error}</div>}
        {loading ? (
          <div className={styles.sysChip}>Loading questions…</div>
        ) : count === 0 ? (
          <div className={styles.sysChip}>No questions yet{isOpen ? ' — be the first to ask one.' : '.'}</div>
        ) : questions.map((q) => (
          <React.Fragment key={q.id}>
            <Bubble
              item={q}
              kind="question"
              isOpen={isOpen}
              onReact={(t) => toggleReaction('questions', q.id, t)}
              onStartEdit={(it) => startEdit(it, 'questions')}
              onDelete={deleteMessage}
              onReply={() => startReply(q.id, q, null)}
              beingEdited={editing?.targetType === 'questions' && editing.id === q.id}
            />
            {!hasInstructorReply(q) && q.answeredAt && <div className={styles.sysChip}>Answered in class</div>}
            {q.replies.map((r) => {
              const target = (r.replyToId && q.replies.find((x) => x.id === r.replyToId)) || q;
              return (
                <Bubble
                  key={r.id}
                  item={r}
                  kind="reply"
                  quote={{ authorName: target.authorName, body: target.body }}
                  isOpen={isOpen}
                  onReact={(t) => toggleReaction('replies', r.id, t)}
                  onStartEdit={(it) => startEdit(it, 'replies')}
                  onDelete={deleteMessage}
                  onReply={() => startReply(q.id, r, r.id)}
                  beingEdited={editing?.targetType === 'replies' && editing.id === r.id}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {isOpen && <StudentAskBar posting={posting} onAsk={postQuestion} editing={editing} onSaveEdit={saveEdit} onCancelEdit={() => setEditing(null)} replyingTo={replyingTo} onSendReply={sendReply} onCancelReply={() => setReplyingTo(null)} />}
    </div>
  );
}
